import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CheckCircle2, Hammer, Package, Pencil, Plus, Receipt, RotateCcw, Undo2, Users, XCircle, Zap } from 'lucide-react'
import { getVendors } from '../../api/master'
import { addRunCost, addRunMaterial, cancelRun, changeRunLine, correctRun, correctionPreview, finishRun, getRun, listProductionProducts, putBack, removeRunLine, reopenRun } from '../../api/production'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import HelpTip from '../../components/HelpTip'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { Card, Tile } from '../../components/Summary'
import { useToast } from '../../components/Toast.jsx'
import { useSession } from '../../session'
import { docNo } from '../../utils/docNumbers'
import { fmtDay, localToday, money } from '../../utils/format'
import { Field } from '../master/masterShared'
import { qty } from './inventoryShared'
import { RUN_STATUS } from './productionShared'
import { ProductionGuide } from './ProductionGuide'
import { CreateOption } from '../../components/CreateOption'


// One production run (a batch): its materials, labour and overhead, the cost
// per unit, and the actions while it's in progress.
export default function ProductionRun() {
  const { runId } = useParams()
  const { has } = useSession()
  const books = has('accounting')
  const canOwe = has('purchase') || books
  const { notify } = useToast()
  const [run, setRun] = useState(null)
  const [error, setError] = useState('')
  const [adding, setAdding] = useState(null) // 'material' | 'cost' | 'back'
  const [editing, setEditing] = useState(null) // a line being changed
  const [removing, setRemoving] = useState(null)
  const [correcting, setCorrecting] = useState(false)
  const [reopenOpen, setReopenOpen] = useState(false)
  const [finishOpen, setFinishOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => { getRun(runId).then(setRun).catch((e) => setError(e.message || 'Could not load the run.')) }, [runId])

  const cancel = async () => {
    setBusy(true)
    try {
      setRun(await cancelRun(runId))
      notify('Run cancelled: stock materials are back in stock')
      setCancelOpen(false)
    } catch (e) {
      setError(e.message || 'Could not cancel the run.')
      setCancelOpen(false)
    } finally {
      setBusy(false)
    }
  }

  if (!run) return <div className="ci-form-page">{error ? <ErrorBanner message={error} /> : <Spinner label="Loading run…" />}</div>
  const st = RUN_STATUS[run.status] || RUN_STATUS.Open
  const open = run.status === 'Open'
  const materials = run.costs.filter((c) => c.kind === 'material')
  const others = run.costs.filter((c) => c.kind !== 'material')
  const labour = run.costs.filter((c) => c.kind === 'labour')
  const overhead = run.costs.filter((c) => c.kind === 'overhead')
  const saved = (msg) => (r) => { setRun(r); setAdding(null); setEditing(null); notify(msg) }
  const lineActions = (c) => open && !c.put_back && (
    <span className="prod-line-actions">
      <button type="button" className="msr-link" onClick={() => setEditing(c)}>Change</button>
      <button type="button" className="msr-link" onClick={() => setRemoving(c)}>Remove</button>
    </span>
  )
  const reopen = async () => {
    setBusy(true)
    try {
      setRun(await reopenRun(runId))
      notify('Run reopened: fix it, then finish it again')
    } catch (e) {
      setError(e.message || 'Could not reopen it.')
    } finally {
      setReopenOpen(false)
      setBusy(false)
    }
  }
  const remove = async () => {
    setBusy(true)
    try {
      setRun(await removeRunLine(runId, removing.cost_id))
      notify('Line removed')
    } catch (e) {
      setError(e.message || 'Could not remove it.')
    } finally {
      setRemoving(null)
      setBusy(false)
    }
  }

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/inventory/production" label="Production" />}
        title={`${run.label} · ${run.product_name}`}
        badge={<StatusBadge label={st.label} tone={st.tone} />}
        meta={[`Started ${fmtDay(run.started_on)}`, run.finished_on && `${run.status === 'Cancelled' ? 'cancelled' : 'finished'} ${fmtDay(run.finished_on)}`, run.notes].filter(Boolean).join(' · ')}
        actions={run.status === 'Finished' ? (
          <div className="ci-header-actions">
            {run.can_reopen && <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setReopenOpen(true)}><RotateCcw size={14} />Reopen</button>}
            <button type="button" className="ci-btn ci-btn-primary ci-btn-sm" onClick={() => setCorrecting(true)}><Pencil size={14} />Correct the cost</button>
          </div>
        ) : open && (
          <div className="ci-header-actions">
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setAdding('material')}><Plus size={14} />Add materials</button>
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setAdding('labour')}><Plus size={14} />Add labour</button>
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setAdding('overhead')}><Plus size={14} />Add overhead</button>
            {(run.returnable.stock.length > 0 || run.returnable.bought.length > 0) && <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setAdding('back')}><Undo2 size={14} />Put back unused</button>}
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setCancelOpen(true)}><XCircle size={14} />Cancel run</button>
            <button type="button" className="ci-btn ci-btn-primary ci-btn-sm" onClick={() => setFinishOpen(true)}><CheckCircle2 size={14} />Finish</button>
          </div>
        )}
      />
      {error && <ErrorBanner message={error} />}
      <ProductionGuide />
      {run.status === 'Finished' && (
        <p className="prod-next">
          Finished. Found a mistake or a cost that came in late? {run.can_reopen
            ? <>Nothing it made has been sold yet, so you can <button type="button" className="msr-link" onClick={() => setReopenOpen(true)}>reopen it</button>, fix it and finish again, or <button type="button" className="msr-link" onClick={() => setCorrecting(true)}>correct the cost</button>.</>
            : <>{run.reopen_blocker} <button type="button" className="msr-link" onClick={() => setCorrecting(true)}>Correct the cost</button>.</>}
          {' '}<HelpTip text="Reopening undoes finishing: the units come back out of stock and the run is in progress again. Correcting the cost leaves it finished and spreads the difference to where its units are now: those still in stock change cost, and those already sold or used change cost of goods sold this month." />
        </p>
      )}
      {open && <NextStep run={run} materials={materials} others={others} onMaterials={() => setAdding('material')} onCost={() => setAdding('labour')} onFinish={() => setFinishOpen(true)} />}

      <div className="sum-tiles prod-tiles">
        <Tile label={run.status === 'Finished' ? 'Made' : 'Making'} icon={Package}
          value={run.status === 'Finished' ? qty(run.made_qty) : qty(run.planned_qty)}
          note={run.status === 'Finished' ? (run.scrap_qty > 0 ? `${qty(run.scrap_qty)} wasted${run.waste_as_loss ? ', recorded as a loss' : ''}` : 'None wasted') : 'Planned'} />
        <Tile label="Materials" icon={Hammer} value={money(run.totals.material)} note={materials.length ? `${materials.length} ${materials.length === 1 ? 'line' : 'lines'}` : 'None added yet'} />
        <Tile label="Labour" icon={Users} value={money(run.totals.labour)} note={labour.length ? `${labour.length} ${labour.length === 1 ? 'line' : 'lines'}` : 'None added yet'} />
        <Tile label="Overhead" icon={Zap} value={money(run.totals.overhead)} note={overhead.length ? `${overhead.length} ${overhead.length === 1 ? 'line' : 'lines'}` : 'None added yet'} />
        <Tile label="Cost per unit" icon={Receipt} value={run.unit_cost != null ? money(run.unit_cost) : '–'}
          note={run.status === 'Finished'
            ? `${money(run.totals.all - (run.waste_as_loss ? lossOf(run) : 0))} ÷ ${qty(run.made_qty)} good`
            : open ? `So far: ${money(run.totals.all)} ÷ ${qty(run.planned_qty)} planned` : 'Cancelled'} />
      </div>
      <CostSplit run={run} />

      <Card title={<>Materials <HelpTip text="Direct materials: what the product is made of, such as fabric, thread, buttons or labels. From stock: taken out at what they cost (oldest stock first). Bought for this batch: recorded as a raw materials purchase, then moved into the run the same day." /></>}
        action={open && <button type="button" className="sum-card-link" onClick={() => setAdding('material')}>Add materials</button>}>
        {materials.length ? (
          <div className="ci-table-scroll">
            <table className="ci-table fit-sm">
              <thead><tr><th>Material</th><th className="num">Quantity</th><th className="num hide-sm">Cost each</th><th className="num">Cost</th></tr></thead>
              <tbody>
                {materials.map((c) => (
                  <tr key={c.cost_id}>
                    <td>{c.put_back ? c.description : (c.product_name || c.description)}<LineMeta line={c} books={books} parts={[c.correction ? 'Correction after finishing' : c.put_back ? 'Put back into stock' : c.product_id ? 'From stock' : 'Bought for this batch', fmtDay(c.date)]} /></td>
                    <td className="num">{c.qty != null ? qty(c.qty) : '–'}</td>
                    <td className="num hide-sm">{c.unit_cost != null ? money(c.unit_cost) : '–'}</td>
                    <td className="num">{money(c.amount)}{lineActions(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="sum-muted">{open ? 'None yet. Add what this batch used: from stock, or what you bought for it.' : 'None.'}</p>}
      </Card>

      <CostCard title="Labour" help={LABOUR_HELP} lines={labour} open={open} books={books} lineActions={lineActions}
        add={() => setAdding('labour')} empty="None yet. Add the pay of the people who made this batch, such as tailors or machinists." />
      <CostCard title="Overhead" help={OVERHEAD_HELP} lines={overhead} open={open} books={books} lineActions={lineActions}
        add={() => setAdding('overhead')} empty="None yet. Add the other costs of making this batch, such as power, rent share, machine hire or packaging." />

      {books && run.finish_je_id && (
        <p className="doc-hint">Finishing entry: <Link to={`/accounting/journal/${run.finish_je_id}`}>{docNo('je', run.finish_je_no)}</Link></p>
      )}

      {adding === 'material' && <AddMaterials run={run} canOwe={canOwe} onClose={() => setAdding(null)} onSaved={saved('Materials added')} />}
      {adding === 'back' && <PutBack run={run} onClose={() => setAdding(null)} onSaved={saved('Put back into stock')} />}
      {(adding === 'labour' || adding === 'overhead') && <AddCost run={run} kind={adding} books={books} onClose={() => setAdding(null)} onSaved={saved(adding === 'labour' ? 'Labour added' : 'Overhead added')} />}
      {editing && editing.kind === 'material' && <AddMaterials run={run} canOwe={canOwe} line={editing} onClose={() => setEditing(null)} onSaved={saved('Line changed')} />}
      {editing && editing.kind !== 'material' && <AddCost run={run} line={editing} books={books} onClose={() => setEditing(null)} onSaved={saved('Line changed')} />}
      {correcting && <CorrectCost run={run} canOwe={canOwe} books={books} onClose={() => setCorrecting(false)} onSaved={(r) => { setRun(r); setCorrecting(false); notify('Cost corrected') }} />}
      <Modal
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        title="Reopen this run?"
        subtitle={`The ${qty(run.made_qty)} ${run.product_name} it made come back out of stock and the run is in progress again. Fix what you need, then finish it again.`}
        footer={<>
          <Button variant="ghost" onClick={() => setReopenOpen(false)}>Keep it finished</Button>
          <Button onClick={reopen} loading={busy}><RotateCcw size={14} />Reopen</Button>
        </>}
      />
      <Modal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="Remove this line?"
        subtitle={removing ? `${removing.product_name || removing.description} (${money(removing.amount)}) comes off the run${removing.product_id ? ', and the material goes back into stock' : ''}${removing.paid_by === 'owed' ? ', and its unpaid bill is voided' : ''}.` : ''}
        footer={<>
          <Button variant="ghost" onClick={() => setRemoving(null)}>Keep it</Button>
          <Button variant="danger" onClick={remove} loading={busy}>Remove</Button>
        </>}
      />
      {finishOpen && <FinishRun run={run} onClose={() => setFinishOpen(false)} onSaved={(r) => { setRun(r); setFinishOpen(false); notify(`${qty(r.made_qty)} ${r.product_name} added to stock`) }} />}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel this run?"
        subtitle="Materials taken from stock go back into stock. Everything else already spent (materials bought for it, labour, overhead) is recorded as a production loss."
        footer={<>
          <Button variant="ghost" onClick={() => setCancelOpen(false)}>Keep it</Button>
          <Button variant="danger" onClick={cancel} loading={busy}>Cancel run</Button>
        </>}
      />
    </div>
  )
}

const LABOUR_HELP = 'Direct labour: the pay of the people who make the product, such as tailors, machinists or bakers, for the time they spent on this batch. Pay for people who don’t make it (a shop assistant, an accountant) is an expense, not labour. In the books it’s recorded in Factory labour, then moved into the run.'
const OVERHEAD_HELP = 'Overhead (manufacturing overhead) is every other cost of making the batch that isn’t materials or the makers’ pay: power for the machines, the workshop’s rent share, machine hire or repairs, needles and other small supplies, packaging. Add what this batch used, such as its share of the month’s power bill. Costs of selling or running the office (adverts, delivery to customers, admin) aren’t overhead: record them as expenses. In the books overhead is recorded in Manufacturing overhead, then moved into the run; overhead already recorded there (a bill, depreciation, insurance) is added with Paid by: Already recorded.'

/** Each unit's cost in its three parts: materials, labour and overhead. */
function CostSplit({ run }) {
  const total = run.totals.all
  if (!(total > 0) || run.unit_cost == null) return null
  const part = (v) => money((run.unit_cost * v) / total)
  return (
    <p className="prod-split">
      <strong>{run.status === 'Finished' ? 'Each one cost' : 'Each one so far'}:</strong>{' '}
      {part(run.totals.material)} materials + {part(run.totals.labour)} labour + {part(run.totals.overhead)} overhead = <strong>{money(run.unit_cost)}</strong>
    </p>
  )
}

/** Labour or overhead lines, in their own card. */
/** A run line's kind and date, and (with Accounting) links to its journal
 * entries: bought materials, labour and overhead have two, recorded (or
 * bought) and then moved into the run; one already recorded in its own
 * account has only the move. */
function LineMeta({ line, books, parts }) {
  const text = parts.filter(Boolean).join(' · ')
  const back = line.amount < 0  // a credit back
  return (
    <span className="cell-sub">
      {text}
      {books && line.je_id && <>{text && ' · '}<Link to={`/accounting/journal/${line.je_id}`}>{docNo('je', line.je_no)}</Link>{line.issue_je_id && (back ? ' credit back' : line.kind === 'material' ? ' bought' : ' recorded')}{!line.issue_je_id && line.paid_by === 'recorded' && (back ? ' back out of the run' : ' into the run')}</>}
      {books && line.issue_je_id && <> · <Link to={`/accounting/journal/${line.issue_je_id}`}>{docNo('je', line.issue_je_no)}</Link>{back ? ' back out of the run' : ' into the run'}</>}
    </span>
  )
}

function CostCard({ title, help, lines, open, books, lineActions, add, empty }) {
  return (
    <Card title={<>{title} <HelpTip text={help} /></>} action={open && <button type="button" className="sum-card-link" onClick={add}>Add {title.toLowerCase()}</button>}>
      {lines.length ? (
        <ul className="sum-list">
          {lines.map((c) => (
            <li key={c.cost_id}>
              <span className="sum-list-main">{c.description}<LineMeta line={c} books={books} parts={[c.correction ? 'Correction after finishing' : '', fmtDay(c.date)]} /></span>
              <span className="sum-list-side">{money(c.amount)}{lineActions(c)}</span>
            </li>
          ))}
        </ul>
      ) : <p className="sum-muted">{open ? empty : 'None.'}</p>}
    </Card>
  )
}

/** The finished run's loss on unusual waste (what didn't go into stock). */
const lossOf = (run) => (run.status === 'Finished' && run.waste_as_loss ? Math.max(0, run.totals.all - run.unit_cost * run.made_qty) : 0)

/** What to do next on an open run, in one line. */
function NextStep({ run, materials, others, onMaterials, onCost, onFinish }) {
  const link = (label, fn) => <button type="button" className="msr-link" onClick={fn}>{label}</button>
  let text
  if (!materials.length) text = <>Next: {link('add the materials', onMaterials)} this batch used, from stock or what you bought for it.</>
  else if (!others.length) text = <>Next: {link('add labour and overhead', onCost)} (the makers’ pay, power, machine hire), or {link('finish', onFinish)} if there were none.</>
  else text = <>When the batch is made, {link('finish it', onFinish)}: say how many came out good, and they go into stock at {money(run.totals.all)} ÷ the good units.</>
  return <p className="prod-next">{text}</p>
}

/** Where labour or overhead is recorded before it goes into a run. */
const OWN_ACCOUNT = { labour: 'Factory labour', overhead: 'Manufacturing overhead' }

/** Paid by: cash, transfer or card, or (with somewhere to pay bills) not
 * paid yet. With Accounting, labour can be owed to workers (wages payable),
 * and labour or overhead already recorded in its own account is only moved
 * into the run. */
function PaidBy({ value, onChange, canOwe, kind, books, error }) {
  return (
    <Field label="Paid by" error={error}>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="cash">Cash</option>
        <option value="transfer">Transfer</option>
        <option value="card">Card</option>
        {canOwe && <option value="owed">Not paid yet (owe the vendor)</option>}
        {books && kind === 'labour' && <option value="wages">Owed to workers</option>}
        {books && OWN_ACCOUNT[kind] && <option value="recorded">Already recorded</option>}
      </select>
    </Field>
  )
}

/** A line about where the cost is recorded, for the choices that aren't plain payments. */
function PaidHint({ kind, paidBy }) {
  if (paidBy === 'wages') return <p className="doc-hint">Not paid yet: recorded as wages payable until you pay the workers.</p>
  if (paidBy === 'recorded' && OWN_ACCOUNT[kind]) {
    return <p className="doc-hint">Already in {OWN_ACCOUNT[kind]}{kind === 'overhead' ? ' (from a bill, depreciation or insurance)' : ' (from payroll)'}, so it’s only moved into this run.</p>
  }
  return null
}

function AddMaterials({ run, canOwe, onClose, onSaved, line }) {
  const [mode, setMode] = useState(line ? (line.product_id ? 'stock' : 'bought') : 'bought')
  const [products, setProducts] = useState([])
  const [vendors, setVendors] = useState([])
  const [form, setForm] = useState(line
    ? { product_id: line.product_id ? String(line.product_id) : '', qty: line.qty != null ? String(line.qty) : '', description: line.description || '', amount: String(line.amount),
        paid_by: line.paid_by || 'cash', vendor_id: line.vendor_id ? String(line.vendor_id) : '', due_date: line.due_date || '', bill_number: line.bill_number || '', date: line.date || localToday() }
    : { product_id: '', qty: '', description: '', amount: '', paid_by: 'cash', vendor_id: '', due_date: '', bill_number: '', date: localToday() })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const set = (p) => setForm((f) => ({ ...f, ...p }))
  useEffect(() => {
    listProductionProducts().then((p) => setProducts((p || []).filter((x) => x.product_id !== run.product_id))).catch(() => {})
    getVendors().then((v) => setVendors(v || [])).catch(() => {})
  }, [run.product_id])
  const picked = products.find((p) => String(p.product_id) === form.product_id)

  const save = async () => {
    setSaving(true)
    setErrors({})
    try {
      const body = mode === 'stock'
        ? { product_id: Number(form.product_id) || 0, qty: form.qty, date: form.date }
        : { kind: 'material', description: form.description, amount: form.amount, paid_by: form.paid_by, date: form.date,
          ...(form.paid_by === 'owed' ? { vendor_id: Number(form.vendor_id) || 0, due_date: form.due_date || undefined, bill_number: form.bill_number } : {}) }
      onSaved(line ? await changeRunLine(run.run_id, line.cost_id, body)
        : mode === 'stock' ? await addRunMaterial(run.run_id, body) : await addRunCost(run.run_id, body))
    } catch (e) {
      setErrors(e.fields || { amount: e.message || 'Could not add the materials.' })
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={line ? 'Change materials' : 'Add materials'}
      subtitle={line ? 'The line is taken out of the run and recorded again as you set it here.' : `What ${run.label} used. Bought for this batch: bought into raw materials, then straight into the run. From stock: taken off your shelves at what it cost.`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}>{line ? 'Save change' : <><Plus size={14} />Add materials</>}</Button>
      </>}
    >
      {!line && <div className="segmented" role="radiogroup" aria-label="Where from">
        <button type="button" role="radio" aria-checked={mode === 'bought'} className={mode === 'bought' ? 'active' : ''} onClick={() => setMode('bought')}>Bought for this batch<small>e.g. a roll of fabric</small></button>
        <button type="button" role="radio" aria-checked={mode === 'stock'} className={mode === 'stock' ? 'active' : ''} onClick={() => setMode('stock')}>From stock<small>Materials you keep</small></button>
      </div>}
      {mode === 'stock' ? (
        <div className="ci-form-meta-top two">
          <Field label="Material" error={errors.product_id}>
            <select value={form.product_id} disabled={!!line} onChange={(e) => set({ product_id: e.target.value })}>
              <option value="">Choose…</option>
              {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name} ({qty(p.qty_on_hand)} in stock)</option>)}
            </select>
          </Field>
          <Field label={<>How much was used <HelpTip text="In the material's own unit, such as metres or pieces. It leaves stock now, at what it cost (oldest stock first)." /></>} error={errors.qty} hint={picked ? `About ${money(picked.avg_cost * Number(form.qty || 0))}` : undefined}>
            <input inputMode="decimal" value={form.qty} onChange={(e) => set({ qty: e.target.value })} placeholder="0" />
          </Field>
        </div>
      ) : (
        <>
          <Field label="What" error={errors.description} hint="e.g. 60 m cotton jersey, thread and labels">
            <input value={form.description} onChange={(e) => set({ description: e.target.value })} />
          </Field>
          <div className="ci-form-meta-top two">
            <Field label="Amount" error={errors.amount}>
              <input inputMode="decimal" value={form.amount} onChange={(e) => set({ amount: e.target.value })} placeholder="0.00" />
            </Field>
            <PaidBy value={form.paid_by} onChange={(v) => set({ paid_by: v })} canOwe={canOwe} error={errors.paid_by || errors.paid_from_code} />
          </div>
          {form.paid_by === 'owed' && (
            <div className="ci-form-meta-top three">
              <Field label="Owed to" error={errors.vendor_id}>
                <select value={form.vendor_id} onChange={(e) => set({ vendor_id: e.target.value })}>
                  <option value="">Choose the vendor…</option>
                  <CreateOption kind="vendor" />
                  {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
                </select>
              </Field>
              <Field label="Due date" error={errors.due_date}>
                <input type="date" value={form.due_date || form.date} min={form.date} onChange={(e) => set({ due_date: e.target.value })} />
              </Field>
              <Field label="Their bill number (optional)">
                <input value={form.bill_number} onChange={(e) => set({ bill_number: e.target.value })} />
              </Field>
            </div>
          )}
        </>
      )}
      <Field label="Date" error={errors.date}>
        <input type="date" value={form.date} max={localToday()} onChange={(e) => set({ date: e.target.value })} />
      </Field>
      {mode === 'bought' && form.paid_by === 'owed' && <p className="doc-hint">A bill is made for the vendor, paid later under Purchase like any other.</p>}
    </Modal>
  )
}

function PutBack({ run, onClose, onSaved }) {
  const { stock, bought } = run.returnable
  const [mode, setMode] = useState(stock.length ? 'stock' : 'bought')
  const [products, setProducts] = useState([])
  const [form, setForm] = useState({ product_id: stock[0] ? String(stock[0].product_id) : '', cost_id: bought[0] ? String(bought[0].cost_id) : '',
    qty: '', bought_qty: '', target: 'new', new_name: '', target_id: '' })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const set = (p) => setForm((f) => ({ ...f, ...p }))
  useEffect(() => { listProductionProducts().then((p) => setProducts((p || []).filter((x) => x.product_id !== run.product_id && x.stock_kind !== 'made'))).catch(() => {}) }, [run.product_id])
  const line = stock.find((r) => String(r.product_id) === form.product_id)
  const buy = bought.find((b) => String(b.cost_id) === form.cost_id)
  const left = Number(form.qty || 0)
  const of = Number(form.bought_qty || 0)
  const value = mode === 'stock'
    ? (line ? Math.min(line.amount, line.unit_cost * left) : 0)
    : (buy && of > 0 ? Math.min(buy.left, (buy.amount * left) / of) : 0)

  const save = async () => {
    setSaving(true)
    setErrors({})
    try {
      onSaved(await putBack(run.run_id, mode === 'stock'
        ? { source: 'stock', product_id: Number(form.product_id) || 0, qty: form.qty }
        : { source: 'bought', cost_id: Number(form.cost_id) || 0, qty: form.qty, value: Math.round(value * 100) / 100,
          ...(form.target === 'new' ? { new_name: form.new_name } : { product_id: Number(form.target_id) || 0 }) }))
    } catch (e) {
      setErrors(e.fields || { qty: e.message || 'Could not put it back.' })
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Put back unused materials"
      subtitle="What this batch didn’t use goes back into stock as a material, so the batch only carries what it used."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving} disabled={!(value > 0)}><Undo2 size={14} />Put back {value > 0 ? money(value) : ''}</Button>
      </>}
    >
      {stock.length > 0 && bought.length > 0 && (
        <div className="segmented" role="radiogroup" aria-label="Where it came from">
          <button type="button" role="radio" aria-checked={mode === 'stock'} className={mode === 'stock' ? 'active' : ''} onClick={() => setMode('stock')}>Taken from stock</button>
          <button type="button" role="radio" aria-checked={mode === 'bought'} className={mode === 'bought' ? 'active' : ''} onClick={() => setMode('bought')}>Left from a purchase</button>
        </div>
      )}
      {mode === 'stock' ? (
        <div className="ci-form-meta-top two">
          <Field label="Material" error={errors.product_id}>
            <select value={form.product_id} onChange={(e) => set({ product_id: e.target.value })}>
              {stock.map((r) => <option key={r.product_id} value={r.product_id}>{r.name} ({qty(r.qty)} used)</option>)}
            </select>
          </Field>
          <Field label="How much is left over" error={errors.qty} hint={line ? `Back at ${money(line.unit_cost)} each, what it cost` : undefined}>
            <input inputMode="decimal" value={form.qty} onChange={(e) => set({ qty: e.target.value })} placeholder="0" />
          </Field>
        </div>
      ) : (
        <>
          <Field label="Which purchase" error={errors.cost_id}>
            <select value={form.cost_id} onChange={(e) => set({ cost_id: e.target.value })}>
              {bought.map((b) => <option key={b.cost_id} value={b.cost_id}>{b.description} · {money(b.amount)}{b.left < b.amount ? ` (${money(b.left)} left in the run)` : ''}</option>)}
            </select>
          </Field>
          <div className="ci-form-meta-top two">
            <Field label={<>How much was bought <HelpTip text="In any unit, such as metres or pieces: used only to work out the leftover's share of what you paid." /></>}>
              <input inputMode="decimal" value={form.bought_qty} onChange={(e) => set({ bought_qty: e.target.value })} placeholder="e.g. 60" />
            </Field>
            <Field label="How much is left over" error={errors.qty || errors.value} hint={value > 0 ? `Its share: ${money(value)}` : undefined}>
              <input inputMode="decimal" value={form.qty} onChange={(e) => set({ qty: e.target.value })} placeholder="e.g. 10" />
            </Field>
          </div>
          <div className="segmented" role="radiogroup" aria-label="Keep it as">
            <button type="button" role="radio" aria-checked={form.target === 'new'} className={form.target === 'new' ? 'active' : ''} onClick={() => set({ target: 'new' })}>A new material</button>
            <button type="button" role="radio" aria-checked={form.target === 'existing'} className={form.target === 'existing' ? 'active' : ''} onClick={() => set({ target: 'existing' })}>One I already keep</button>
          </div>
          {form.target === 'new' ? (
            <Field label="Name it" error={errors.product_id} hint="With its unit, e.g. “Cotton jersey (m)”. It’s added to your stock as a material.">
              <input value={form.new_name} onChange={(e) => set({ new_name: e.target.value })} />
            </Field>
          ) : (
            <Field label="Material" error={errors.product_id}>
              <select value={form.target_id} onChange={(e) => set({ target_id: e.target.value })}>
                <option value="">Choose…</option>
                {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
              </select>
            </Field>
          )}
        </>
      )}
    </Modal>
  )
}

function AddCost({ run, onClose, onSaved, line, books, kind = 'labour' }) {
  const [form, setForm] = useState(line
    ? { kind: line.kind, amount: String(line.amount), description: line.description || '', paid_by: line.paid_by === 'owed' ? 'cash' : (line.paid_by || 'cash'), date: line.date || localToday() }
    : { kind, amount: '', description: '', paid_by: 'cash', date: localToday() })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const set = (p) => setForm((f) => ({ ...f, ...p }))
  const save = async () => {
    setSaving(true)
    setErrors({})
    try {
      onSaved(line ? await changeRunLine(run.run_id, line.cost_id, form) : await addRunCost(run.run_id, form))
    } catch (e) {
      setErrors(e.fields || { amount: e.message || 'Could not add the cost.' })
      setSaving(false)
    }
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={`${line ? 'Change' : 'Add'} ${form.kind === 'overhead' ? 'overhead' : 'labour'}`}
      subtitle={line ? 'The line is taken out of the run and recorded again as you set it here.' : `Part of what ${run.label} costs to make.`}
      size="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}>{line ? 'Save change' : <><Plus size={14} />Add</>}</Button>
      </>}
    >
      <div className="segmented" role="radiogroup" aria-label="Kind of cost">
        {[['labour', 'Labour'], ['overhead', 'Overhead']].map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={form.kind === k} className={form.kind === k ? 'active' : ''}
            onClick={() => set({ kind: k, ...(form.paid_by === 'wages' || form.paid_by === 'recorded' ? { paid_by: 'cash' } : {}) })}>{l}</button>
        ))}
      </div>
      <p className="doc-hint">{form.kind === 'overhead' ? <>Overhead: the other costs of making this batch, not the people or the materials. <HelpTip text={OVERHEAD_HELP} /></> : <>Labour: pay for the people who made this batch. <HelpTip text={LABOUR_HELP} /></>}</p>
      <Field label="What for" error={errors.description} hint={form.kind === 'labour' ? 'e.g. Tailors, 3 days' : 'e.g. Power, rent share, machine hire'}>
        <input value={form.description} onChange={(e) => set({ description: e.target.value })} />
      </Field>
      <div className="ci-form-meta-top two even">
        <Field label="Amount" error={errors.amount}>
          <input inputMode="decimal" value={form.amount} onChange={(e) => set({ amount: e.target.value })} placeholder="0.00" />
        </Field>
        <PaidBy value={form.paid_by} onChange={(v) => set({ paid_by: v })} canOwe={false} kind={form.kind} books={books} error={errors.paid_by || errors.paid_from_code} />
      </div>
      <PaidHint kind={form.kind} paidBy={form.paid_by} />
      <Field label="Date" error={errors.date}>
        <input type="date" value={form.date} max={localToday()} onChange={(e) => set({ date: e.target.value })} />
      </Field>
    </Modal>
  )
}

function CorrectCost({ run, canOwe, books, onClose, onSaved }) {
  const [form, setForm] = useState({ direction: 'more', kind: 'labour', description: '', amount: '', paid_by: 'cash', vendor_id: '', due_date: '', bill_number: '', date: localToday() })
  const [vendors, setVendors] = useState([])
  const [preview, setPreview] = useState(null)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const set = (p) => setForm((f) => ({ ...f, ...p }))
  useEffect(() => { getVendors().then((v) => setVendors(v || [])).catch(() => {}) }, [])
  const signed = (Number(form.amount) || 0) * (form.direction === 'less' ? -1 : 1)
  useEffect(() => {
    if (!signed) { setPreview(null); return }
    const t = setTimeout(() => correctionPreview(run.run_id, signed).then(setPreview).catch(() => setPreview(null)), 250)
    return () => clearTimeout(t)
  }, [run.run_id, signed])
  const less = form.direction === 'less'

  const save = async () => {
    setSaving(true)
    setErrors({})
    try {
      onSaved(await correctRun(run.run_id, { ...form, ...(form.paid_by === 'owed' ? { vendor_id: Number(form.vendor_id) || 0, due_date: form.due_date || undefined } : {}) }))
    } catch (e) {
      setErrors(e.fields || { amount: e.message || 'Could not correct the cost.' })
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Correct the cost"
      subtitle={`${run.label} stays finished. The difference goes to where the ${qty(run.made_qty)} it made are now: in stock, or already sold or used.`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving} disabled={!signed}><Pencil size={14} />Correct the cost</Button>
      </>}
    >
      <div className="segmented" role="radiogroup" aria-label="More or less">
        <button type="button" role="radio" aria-checked={!less} className={!less ? 'active' : ''} onClick={() => set({ direction: 'more' })}>It cost more<small>A missed or late cost</small></button>
        <button type="button" role="radio" aria-checked={less} className={less ? 'active' : ''} onClick={() => set({ direction: 'less', paid_by: form.paid_by === 'owed' ? 'cash' : form.paid_by })}>It cost less<small>A refund, or a cost entered twice</small></button>
      </div>
      <div className="ci-form-meta-top two">
        <Field label="What kind" error={errors.kind}>
          <select value={form.kind} onChange={(e) => set({ kind: e.target.value, ...(form.paid_by === 'wages' || form.paid_by === 'recorded' ? { paid_by: 'cash' } : {}) })}>
            <option value="material">Materials</option>
            <option value="labour">Labour</option>
            <option value="overhead">Overhead</option>
          </select>
        </Field>
        <Field label="Amount" error={errors.amount}>
          <input inputMode="decimal" value={form.amount} onChange={(e) => set({ amount: e.target.value })} placeholder="0.00" />
        </Field>
      </div>
      <Field label="What for" error={errors.description} hint={less ? 'e.g. Fabric refund from the supplier' : 'e.g. Tailors’ overtime, paid late'}>
        <input value={form.description} onChange={(e) => set({ description: e.target.value })} />
      </Field>
      <div className="ci-form-meta-top two">
        {less ? (
          <Field label="Money came back to" error={errors.paid_by || errors.paid_from_code}>
            <select value={form.paid_by} onChange={(e) => set({ paid_by: e.target.value })}>
              <option value="cash">Cash</option>
              <option value="transfer">Bank (transfer)</option>
              <option value="card">Bank (card)</option>
              {books && form.kind === 'labour' && <option value="wages">Less owed to workers</option>}
              {books && OWN_ACCOUNT[form.kind] && <option value="recorded">Back to its account</option>}
            </select>
          </Field>
        ) : <PaidBy value={form.paid_by} onChange={(v) => set({ paid_by: v })} canOwe={canOwe} kind={form.kind} books={books} error={errors.paid_by || errors.paid_from_code} />}
        <Field label="Date" error={errors.date}>
          <input type="date" value={form.date} max={localToday()} onChange={(e) => set({ date: e.target.value })} />
        </Field>
      </div>
      {!less && form.paid_by === 'owed' && (
        <div className="ci-form-meta-top three">
          <Field label="Owed to" error={errors.vendor_id}>
            <select value={form.vendor_id} onChange={(e) => set({ vendor_id: e.target.value })}>
              <option value="">Choose the vendor…</option>
              <CreateOption kind="vendor" />
              {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
            </select>
          </Field>
          <Field label="Due date" error={errors.due_date}>
            <input type="date" value={form.due_date || form.date} min={form.date} onChange={(e) => set({ due_date: e.target.value })} />
          </Field>
          <Field label="Their bill number (optional)">
            <input value={form.bill_number} onChange={(e) => set({ bill_number: e.target.value })} />
          </Field>
        </div>
      )}
      {!less && <PaidHint kind={form.kind} paidBy={form.paid_by} />}
      {less && <p className="doc-hint">A credit from a supplier you haven’t paid yet is recorded under Purchase instead.</p>}
      {preview && (
        <div className="prod-correct-preview">
          <strong>Where the {money(Math.abs(signed))} goes</strong>
          <ul>
            {Math.abs(preview.stock) >= 0.005 && <li>{money(Math.abs(preview.stock))} to the {qty(preview.units_in_stock)} still in stock ({preview.per_unit >= 0 ? '+' : '−'}{money(Math.abs(preview.per_unit))} each)</li>}
            {Math.abs(preview.sold) >= 0.005 && <li>{money(Math.abs(preview.sold))} to cost of goods sold, for the {qty(preview.units_gone)} sold or used <HelpTip text="They’ve already left stock, so their share is a correction to this month’s cost of goods sold." /></li>}
            {Math.abs(preview.losses) >= 0.005 && <li>{money(Math.abs(preview.losses))} to production losses (the unusual waste’s share)</li>}
          </ul>
          <span>New cost per unit: <strong>{money(preview.new_unit_cost)}</strong> (was {money(run.unit_cost)})</span>
        </div>
      )}
    </Modal>
  )
}

function FinishRun({ run, onClose, onSaved }) {
  const [made, setMade] = useState(String(run.planned_qty))
  const [scrap, setScrap] = useState('0')
  const [asLoss, setAsLoss] = useState(false)
  const [day, setDay] = useState(localToday())
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const good = Number(made || 0)
  const wasted = Number(scrap || 0)
  const lossPart = asLoss && wasted > 0 && good + wasted > 0 ? (run.totals.all * wasted) / (good + wasted) : 0
  const each = good > 0 ? (run.totals.all - lossPart) / good : null
  const save = async () => {
    setSaving(true)
    setErrors({})
    try {
      onSaved(await finishRun(run.run_id, { made_qty: made, scrap_qty: scrap || 0, waste_as_loss: asLoss, date: day }))
    } catch (e) {
      setErrors(e.fields || { made_qty: e.message || 'Could not finish the run.' })
      setSaving(false)
    }
  }
  return (
    <Modal
      open
      onClose={onClose}
      title="Finish the run"
      subtitle={`The good ${run.product_name} go into stock at the run's total cost ÷ good units.`}
      size="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}><CheckCircle2 size={14} />Finish</Button>
      </>}
    >
      <div className="ci-form-meta-top two">
        <Field label={<>Good units made <HelpTip text="The ones you can sell. They go into stock at the run’s total cost ÷ this number." /></>} error={errors.made_qty}>
          <input inputMode="decimal" value={made} onChange={(e) => setMade(e.target.value)} />
        </Field>
        <Field label={<>Spoiled <HelpTip text="Pieces that came out unusable. Normal spoilage stays in the good units’ cost; tick below if it was unusual." /></>} error={errors.scrap_qty}>
          <input inputMode="decimal" value={scrap} onChange={(e) => setScrap(e.target.value)} />
        </Field>
      </div>
      {wasted > 0 && (
        <label className="ci-form-toggle msr-toggle">
          <input type="checkbox" checked={asLoss} onChange={(e) => setAsLoss(e.target.checked)} />
          <span>
            <strong>This waste was unusual</strong>{' '}
            <HelpTip text="Normal waste (a few spoiled pieces in every batch) is part of what the good ones cost, so it's left in their cost. Unusual waste, such as a machine fault, is recorded as a production loss instead, so it doesn't make the good ones look dearer." />
            <span className="cell-sub">Record the wasted units as a loss instead of in the good units’ cost.</span>
          </span>
        </label>
      )}
      <Field label="Finished on" error={errors.date}>
        <input type="date" value={day} min={run.started_on} max={localToday()} onChange={(e) => setDay(e.target.value)} />
      </Field>
      <p className="doc-hint">Total cost {money(run.totals.all)}{each != null && <> · {money(each)} each</>}{lossPart > 0 && <> · {money(lossPart)} loss</>}</p>
    </Modal>
  )
}
